import { request, uploadAssetBytes } from './client';
import type {
  CaptureAssetsResponse,
  CaptureDetailResponse,
  CaptureJob,
  CaptureQueueRow,
  CaptureRecord,
  ConfirmCaptureRequest,
  ConfirmCaptureResponse,
  CaptureSubmission,
  CreateVoiceNoteRequest,
  CreateVoiceNoteResponse,
  EscalateResponse,
  FrameChange,
  ListCaptureJobsQuery,
  ListCapturesQuery,
  ListJobAttachmentsResponse,
  ListJobCapturesQuery,
  MyAccess,
  Page,
  PartnerCaptureManifest,
  ReExtractionAccepted,
  RecaptureResponse,
  RerunRequest,
  SmartCaptureSettings,
  SubmitCaptureResponse,
  TranscribeSessionRequest,
  TranscribeSessionResponse,
  UploadUrlRequest,
  UploadUrlResponse,
  VerdictRequest,
  VerdictResponse,
  VoiceNoteUploadUrlRequest,
  VoiceNoteUploadUrlResponse,
} from './types';

/**
 * Typed client for every route in INTERFACES.md section 3, base path
 * `/capture`. One function per route, named after the route's job rather
 * than its HTTP verb, so a caller reads intent at the call site.
 */

export function requestUploadUrl(body: UploadUrlRequest): Promise<UploadUrlResponse> {
  return request<UploadUrlResponse>('/capture/upload-url', { method: 'POST', body });
}

/**
 * The caller's own resolved access (OPERATOR_ACCESS.md section 3). Read once
 * after sign-in by `AccessProvider`, never polled: the capture API enforces
 * the same rights on every route this answer only spares a control that
 * would refuse the caller.
 */
export function getMyAccess(): Promise<MyAccess> {
  return request<MyAccess>('/capture/me');
}

/**
 * Creates a capture in a Capture Job (CAPTURE_JOBS.md section 1). The API
 * answers 400 without a job, 404 for an unknown one, 409 for a CLOSED one,
 * and 403 unless the caller is assigned to it or holds `reviewCaptures`.
 */
export function createCapture(
  captureJobId: string,
  body: Omit<CaptureSubmission, 'captureJobId'>
): Promise<SubmitCaptureResponse> {
  const submission: CaptureSubmission = { ...body, captureJobId };
  return request<SubmitCaptureResponse>('/capture', { method: 'POST', body: submission });
}

/**
 * The Capture Jobs the caller may work in (CAPTURE_JOBS.md section 2). An
 * operator gets the OPEN jobs assigned to them, a caller holding
 * `reviewCaptures` every job. Each carries its counts.
 */
export function listCaptureJobs(query: ListCaptureJobsQuery = {}): Promise<CaptureJob[]> {
  return request<CaptureJob[]>('/capture-jobs', { query: { status: query.status } });
}

/**
 * The files attached to a job, each with a 15-minute download URL
 * (CAPTURE_JOBS.md section 8.2). 403 unless assigned or `reviewCaptures`.
 */
export function listJobAttachments(jobId: string): Promise<ListJobAttachmentsResponse> {
  return request<ListJobAttachmentsResponse>(`/capture-jobs/${encodeURIComponent(jobId)}/attachments`);
}

/** One Capture Job with its counts. 403 unless assigned or `reviewCaptures`. */
export function getCaptureJob(jobId: string): Promise<CaptureJob> {
  return request<CaptureJob>(`/capture-jobs/${encodeURIComponent(jobId)}`);
}

/** The captures already taken for a job, a page at a time. */
export function listJobCaptures(jobId: string, query: ListJobCapturesQuery = {}): Promise<Page<CaptureQueueRow>> {
  return request<Page<CaptureQueueRow>>(`/capture-jobs/${encodeURIComponent(jobId)}/captures`, {
    query: { status: query.status, cursor: query.cursor },
  });
}

/**
 * A presigned Amazon Transcribe streaming WebSocket URL for live voice
 * (CAPTURE_JOBS.md section 3), valid for 5 minutes, PCM 16 kHz mono. The
 * final text is saved with `createVoiceNote` as a `transcript` with no
 * `audioKey`.
 */
export function startTranscribeSession(body: TranscribeSessionRequest = {}): Promise<TranscribeSessionResponse> {
  return request<TranscribeSessionResponse>('/capture/transcribe-session', { method: 'POST', body });
}

/**
 * The record plus `attention` and `qualityCard` (INTERFACES.md 15.2). Both
 * derived views are optional on the wire, so a caller renders the bare
 * record when an older API omits them.
 */
export function getCapture(captureId: string): Promise<CaptureDetailResponse> {
  return request<CaptureDetailResponse>(`/capture/${encodeURIComponent(captureId)}`);
}

/**
 * Short-lived read URLs for the frames on a capture, in capture order.
 *
 * The raw asset bucket is private and no object in it is ever public, so a
 * screen that shows the operator what they photographed asks for a fresh
 * URL per visit rather than holding one.
 */
export function getCaptureAssets(captureId: string): Promise<CaptureAssetsResponse> {
  return request<CaptureAssetsResponse>(`/capture/${encodeURIComponent(captureId)}/assets`);
}

export function listCaptures(query: ListCapturesQuery = {}): Promise<Page<CaptureRecord>> {
  return request<Page<CaptureRecord>>('/capture', {
    query: { status: query.status, sessionId: query.sessionId, cursor: query.cursor },
  });
}

export function requestRecapture(captureId: string, reason: string): Promise<RecaptureResponse> {
  return request<RecaptureResponse>(`/capture/${encodeURIComponent(captureId)}/recapture`, {
    method: 'POST',
    body: { reason },
  });
}

/**
 * The operator's sign-off on the review route, while they can still look at
 * the item (INTERFACES.md 15.2). Applies `fieldEdits` and `fieldAdds`, then
 * marks every field verified by the caller. Under ADR-004 this can approve
 * the capture by rule: the response names the resulting status, the lane,
 * the reason code when the capture went to the manager, and the fields still
 * outstanding. Answers 409 from a finished status or while extracting.
 */
export function confirmCapture(
  captureId: string,
  body: ConfirmCaptureRequest = {}
): Promise<ConfirmCaptureResponse> {
  return request<ConfirmCaptureResponse>(`/capture/${encodeURIComponent(captureId)}/confirm`, {
    method: 'POST',
    body,
  });
}

/**
 * Replaces one frame (a retake) or adds one, then re-runs extraction on the
 * same capture id. Three frames remain the maximum. `frame.assetKey` comes
 * from `requestUploadUrl`, the same handshake a new capture uses.
 */
export function changeFrame(captureId: string, frame: FrameChange): Promise<ReExtractionAccepted> {
  return request<ReExtractionAccepted>(`/capture/${encodeURIComponent(captureId)}/frames`, {
    method: 'POST',
    body: frame,
  });
}

/** Rerun AI: re-analyses the named fields, or every attribute when `fields` is empty. */
export function rerunCapture(captureId: string, body: RerunRequest): Promise<ReExtractionAccepted> {
  return request<ReExtractionAccepted>(`/capture/${encodeURIComponent(captureId)}/rerun`, {
    method: 'POST',
    body,
  });
}

/** "Send to manager with a question". Moves the capture to IN_REVIEW with `OPERATOR_ESCALATED`. */
export function escalateCapture(captureId: string, question: string): Promise<EscalateResponse> {
  return request<EscalateResponse>(`/capture/${encodeURIComponent(captureId)}/escalate`, {
    method: 'POST',
    body: { question },
  });
}

/**
 * Presigned upload for one voice clip (INTERFACES.md 15.3). The clip is
 * uploaded with `uploadAssetBytes`, then registered with `createVoiceNote`.
 * Clips cap at 120 seconds and five per capture.
 */
export function requestVoiceNoteUpload(
  captureId: string,
  body: VoiceNoteUploadUrlRequest
): Promise<VoiceNoteUploadUrlResponse> {
  return request<VoiceNoteUploadUrlResponse>(
    `/capture/${encodeURIComponent(captureId)}/voice-notes/upload-url`,
    { method: 'POST', body }
  );
}

/** Registers an uploaded clip and starts its transcription, or saves a live
 * transcription's text when the body carries `transcript` and no `audioKey`. */
export function createVoiceNote(captureId: string, body: CreateVoiceNoteRequest): Promise<CreateVoiceNoteResponse> {
  return request<CreateVoiceNoteResponse>(`/capture/${encodeURIComponent(captureId)}/voice-notes`, {
    method: 'POST',
    body,
  });
}

/** The tenant's Smart Capture settings (INTERFACES.md 15.5). Read only here. */
export function getSmartCaptureSettings(): Promise<SmartCaptureSettings> {
  return request<SmartCaptureSettings>('/capture/settings');
}

/**
 * A reviewer's verdict from the shared review route (ADR-006), through the
 * INTERFACES.md section 12 route the console already calls.
 */
export function submitReviewVerdict(captureId: string, body: VerdictRequest): Promise<VerdictResponse> {
  return request<VerdictResponse>(`/review/captures/${encodeURIComponent(captureId)}/verdict`, {
    method: 'POST',
    body,
  });
}

export function getPartnerManifest(): Promise<PartnerCaptureManifest> {
  return request<PartnerCaptureManifest>('/capture/manifest');
}

/**
 * Requests an upload URL for `bytes` and uploads it, unless the fingerprint
 * is already known to the tenant. `POST /capture/upload-url` is the
 * deduplication point (INTERFACES.md section 3): a `duplicateOf` response
 * means the client never uploads the bytes a second time. Returns the
 * `assetKey` either way, so the caller always has a key to put on the
 * `CaptureSubmission`.
 */
export async function uploadCaptureAsset(input: {
  mode: UploadUrlRequest['mode'];
  fingerprint: string;
  bytes: Blob;
  contentType: string;
  captureJobId: string;
}): Promise<{ assetKey: string; duplicateOf?: string }> {
  const { mode, fingerprint, bytes, contentType, captureJobId } = input;
  const uploadUrlResponse = await requestUploadUrl({
    mode,
    fingerprint,
    contentType,
    byteLength: bytes.size,
    captureJobId,
  });

  if (uploadUrlResponse.duplicateOf) {
    return { assetKey: uploadUrlResponse.assetKey, duplicateOf: uploadUrlResponse.duplicateOf };
  }

  await uploadAssetBytes(uploadUrlResponse.uploadUrl, bytes, contentType);
  return { assetKey: uploadUrlResponse.assetKey };
}
